/**
 * The view a page or figure comment was written on, and how to open it again.
 *
 * Since 2026-10-09 a comment stores the view portably (state/sharedView.ts): its page
 * state and filters with every person as an opaque key. Before, it stored the filter
 * bar as it was — names included, and a slice's whole list of names (review #232: 555
 * of them in one comment). Those comments still open: `viewOfContext` reads either.
 *
 * Either way, opening one shows it within the READER's access. A person the reader is
 * not served is left out, and the app says so (state/ViewLinks.tsx).
 */
import { ALL, EMPTY_FILTERS, readFilters, writeFilterParams, type Filters } from '@/data/filters'
import { PF_KEY, resolveView, viewSearch, type SharedView } from '@/state/sharedView'
import type { PeopleKeys } from '@/state/personKeys'
import type { CommentContext as DrawerContext } from '@focalpointcare/review-drawer'

/**
 * What this app keeps with a comment about its view, beside `search` (the shared
 * drawer adds `datasetVersion` and `figure`; reviewHost.tsx).
 *
 * v2 (2026-10-09): `view`, the view portable — people as opaque keys, a slice as its bar.
 * v1 (no `v`): the whole filter bar, `filters`, names included. Still read.
 */
export interface CpcView {
  v?: 2
  view?: SharedView
  /** v1 only. Never written now: it carried names, and a slice's every name. */
  filters?: Partial<Filters>
}

export type CommentContext = DrawerContext<CpcView>

/** The context a new comment stores: the view, keyed; never a name list. */
export function newContext(view: SharedView, datasetVersion: string | null): CommentContext {
  return { v: 2, view, search: view.search, datasetVersion }
}

/**
 * A comment's view, portable, with how many people in it the reader is not served
 * (an old context names them; a new one carries keys, counted when it is resolved).
 */
export function viewOfContext(
  path: string,
  ctx: CommentContext,
  keys: PeopleKeys,
): { view: SharedView; refused: number } {
  if (ctx.view) return { view: ctx.view, refused: 0 }

  // An old context: the filter bar as it was, names and all.
  const f: Filters = { ...EMPTY_FILTERS, ...(ctx.filters ?? {}) }
  const params = new URLSearchParams(ctx.search ?? '')
  writeFilterParams(params, f)
  const qs = params.toString()
  const view: SharedView = { v: 1, path, search: viewSearch(qs ? `?${qs}` : '') }

  let refused = 0
  const key = (name: string) => {
    const k = keys.toKey(name)
    if (!k) refused++
    return k ? `~${k}~` : null
  }
  const pf: NonNullable<SharedView['pf']> = {}
  if (f.supervisor !== ALL) {
    const k = key(f.supervisor)
    if (k) pf.supervisor = k
  }
  if (f.employee !== ALL) {
    const k = key(f.employee)
    if (k) pf.employee = k
  }
  if (f.slice) {
    // A slice that kept its bar goes as the bar; the reader's crosstab finds its people.
    if (f.slice.match) pf.slice = { label: f.slice.label, match: f.slice.match }
    else {
      const ks: string[] = []
      for (const n of f.slice.employees) {
        const k = keys.toKey(n)
        if (k) ks.push(`~${k}~`)
      }
      // Who in an old list is outside the reader's access is not counted one by one: a
      // slice of the whole book would say "500 people" to everyone who is scoped.
      if (ks.length < f.slice.employees.length) refused++
      pf.slice = { label: f.slice.label, employees: ks }
    }
  }
  if (Object.keys(pf).length) view.pf = pf
  return { view, refused }
}

/** The filters a comment was written under, as this reader can name them. */
export function contextFilters(path: string, ctx: CommentContext | null, keys: PeopleKeys): Filters | null {
  if (!ctx) return null
  if (!ctx.view) return ctx.filters ? { ...EMPTY_FILTERS, ...ctx.filters } : null
  const r = resolveView(viewOfContext(path, ctx, keys).view, keys, null)
  return readFilters({ search: r.search, state: r.state })
}

/** Whether the view on screen is the comment's: same page, query string and people. */
export function sameView(a: SharedView, b: SharedView): boolean {
  return (
    a.path === b.path &&
    viewSearch(a.search) === viewSearch(b.search) &&
    JSON.stringify(a.pf ?? {}) === JSON.stringify(b.pf ?? {})
  )
}

export { PF_KEY }
